// @ts-nocheck
// Legacy, and only in the type-check program because an FSD slice imports it through
// one of the two sanctioned exceptions. Its own types were never checked and fixing
// them means guessing at API semantics — out of scope for the FSD migration. Drop this
// line when the file is migrated.
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  CPMCalculationType,
  AdType,
  MarketplaceStatus,
  OnboardingSteps
} from '@/api/types'
import { deepMerge } from '@/utils/object'
import * as Sentry from '@sentry/vue'

import {
  getUserInfo,
  getUserOtpInfo,
  logout,
  login,
  registration,
  resetPassword,
  confirmAccount,
  sendConfirmation,
  unsubscribe,
  checkSubscription,
  sendRecovery,
  changePassword,
  enableTwoFa,
  disableTwoFa,
  getBack,
  socialAuth,
  setMessageAsRead,
  acceptCurrentTerms,
  requestMarketplace,
  requestCall,
  togglePaymentsStatus,
  toggleFrozenStatus,
  saveNotifications,
  getBalances,
  getTelegramLink,
  disconnectTelegram,
  becomeUser,
  toggleMarket,
  IBalanceItem,
  IBecomePayload,
  IPasswordAttributes,
  ILogin,
  IRegistration,
  IResetPasswordAttributes,
  ITwoFaAttributes,
  IUserAttributes,
  IPaymentUnderReview,
  getBalancesRange,
  getStatistics,
  IStatistics,
  setInitialTags,
  enableApiToken,
  rotateApiToken,
  disableApiToken
} from '@/api/users'

import {
  getCurrentUserInfo,
  saveInfo,
  IUserInfoAttributes
} from '@/api/user_infos'

export const useUserStore = defineStore('user', () => {
  const userId = ref(0)
  const name = ref('')
  const email = ref('')
  const roles = ref('')
  const balance = ref(0)
  const marketplaceBalance = ref(0)
  const serviceFeeBalance = ref(0)
  const payoutsBalance = ref(0)
  const replenishmentsBalance = ref(0)
  const withValidContact = ref(false)
  const paymentsUnderReview = ref<IPaymentUnderReview[]>([])
  const skipCpmCheck = ref(false)
  const allowSmartCpm = ref(false)
  const withValidInfo = ref(false)
  const useConv = ref(false)
  const statConversionTypes = ref<number[]>([])
  const referrerCode = ref('')
  const staff = ref(false)
  const rtb = ref(false)
  const showSystemMessage = ref(false)
  const systemMessage = ref('')
  const info = ref<IUserInfoAttributes | undefined>({ traffic_type: [] })
  const otpInfo = ref({ required: false })
  const suid = ref('')
  const apiToken = ref('')
  const apiEnabled = ref(false)
  const availableCpmTypes = ref<CPMCalculationType[]>([])
  const tourId = ref(0)
  const onboardingStep = ref(0)
  const onboardingStatus = ref(OnboardingSteps.NeedCreatives)
  const termsAccepted = ref(true)
  const notifications = ref<any>({})
  const balances = ref<IBalanceItem[]>([])
  const withConfirmationRequests = ref(false)
  const availableToWithdrawn = ref(0)
  const marketplaceStatus = ref(MarketplaceStatus.Disabled)
  const enabledFeatures = ref<string[]>([])
  const flatDealAdTypeIds = ref<AdType[]>([])
  const hasTabFlatDeal = computed(() =>
    flatDealAdTypeIds.value.includes(AdType.Tab)
  )
  const hadInitialTalk = ref(false)
  const initialTalkRequested = ref(false)
  const showInitialSurveyModal = ref(false)
  const partnersEnabled = ref(false)
  const dealsEnabled = ref(false)
  const flags = ref<string[]>([])
  const adScanHits = ref({
    system: { hits: 0, original_hits: 0 },
    manual: { hits: 0, original_hits: 0 }
  })
  const nextUsageFeeDate = ref('')
  const frozen = ref(true)
  const allowedPayments = ref('none')
  const showWidget = ref(false)
  const widgetData = ref<any>(null)
  const verifyPaymentData = ref<any>(null)
  const isNewDirect = ref(false)
  const testTargetingAllowed = ref(false) // Added missing ref
  const extendedSettingsAllowed = ref(false)
  const usageTaxPayed = ref(false)
  const connectedMessengers = ref<any[]>([])
  const isProcessingOnboarding = ref(false)
  const defaultNotifications = ref({
    impressions: {
      country: { threshold: 10000, factor: 40, notifiers: [] },
      ad_type: { threshold: 10000, factor: 40, notifiers: [] }
    },
    requests: {
      country: { threshold: 10000, factor: 40, notifiers: [] },
      ad_type: { threshold: 10000, factor: 40, notifiers: [] }
    },
    all: {
      country: { threshold: 10000, factor: 40, notifiers: [] },
      ad_type: { threshold: 10000, factor: 40, notifiers: [] }
    },
    reports: {
      ad_type: { schedule: [], notifiers: [] },
      network: { schedule: [], notifiers: [] },
      all: { schedule: [], notifiers: [] }
    },
    finance: {
      low_balance: { notifiers: [], threshold: 3 },
      payout: { notifiers: [] },
      report: { notifiers: [] }
    }
  })
  const referralAdvert = ref<number>(0)
  const referralPublisher = ref<number>(0)
  const unsubscribed = ref<boolean | null>(null)
  const telegram = ref<string | null>(null)
  const isWidgetScriptLoaded = ref(false)

  const updateStateStatistics = (data: IStatistics) => {
    marketplaceBalance.value = data.marketplace_income
    serviceFeeBalance.value = data.service_fee_sum
    payoutsBalance.value = data.paid_amount
    replenishmentsBalance.value = data.replenishments_sum
  }

  const updateState = (data: IUserAttributes, withApiToken = true) => {
    userId.value = data.id
    name.value = data.name
    email.value = data.email
    roles.value = data.roles
    balance.value = data.balance
    withValidContact.value = data.with_valid_contact
    paymentsUnderReview.value = data.payments_under_review || []
    withValidInfo.value = data.with_valid_info
    skipCpmCheck.value = data.skip_cpm_check
    staff.value = data.staff
    rtb.value = data.rtb
    allowSmartCpm.value = data.allow_smart_cpm
    useConv.value = data.use_conv
    statConversionTypes.value = data.stat_conversion_types || []
    availableCpmTypes.value = data.available_cpm_types
    referrerCode.value = data.referrer_code
    showSystemMessage.value = data.show_system_message
    systemMessage.value = data.system_message
    suid.value = data.suid
    apiEnabled.value = data.api_enabled
    if (withApiToken) {
      apiToken.value = data.api_token
    }
    showWidget.value = data.with_widget
    widgetData.value = data.widget_data
    verifyPaymentData.value = data.verify_payment_data
    isNewDirect.value = data.is_new_direct
    testTargetingAllowed.value = data.test_targeting_allowed || false // Added missing assignment
    extendedSettingsAllowed.value = data.extended_settings_allowed
    tourId.value = data.tour_id
    onboardingStep.value = data.onboarding_step || 0
    onboardingStatus.value = data.onboarding_status as OnboardingSteps
    termsAccepted.value = Boolean(data.terms_accepted)
    notifications.value = deepMerge(
      defaultNotifications.value,
      data.notifications
    )
    withConfirmationRequests.value = data.with_confirmation_requests
    availableToWithdrawn.value = data.available_to_withdrawn
    marketplaceStatus.value = data.marketplace_status
    enabledFeatures.value = data.enabled_features
    flatDealAdTypeIds.value = data.flat_deal_ad_type_ids || []
    marketplaceBalance.value = data.marketplace_income
    serviceFeeBalance.value = data.service_fee_sum
    payoutsBalance.value = data.paid_amount
    replenishmentsBalance.value = data.replenishments_sum
    usageTaxPayed.value = !!data.usage_tax_payed
    connectedMessengers.value = data.connected_messengers || []
    hadInitialTalk.value = data.had_initial_talk || false
    initialTalkRequested.value = data.initial_talk_requested || false
    dealsEnabled.value = data.deals_enabled || false
    partnersEnabled.value = data.partners_enabled || false
    adScanHits.value = data.ad_scan_hits || {
      system: { hits: 0, original_hits: 0 },
      manual: { hits: 0, original_hits: 0 }
    }
    nextUsageFeeDate.value = data.next_usage_fee_date
    flags.value = data.flags || []
    frozen.value = data.frozen || false
    allowedPayments.value = data.allowed_payments || 'none'
    referralAdvert.value = data.referral_advert
    referralPublisher.value = data.referral_publisher
    showInitialSurveyModal.value = data.show_initial_survey_modal
    telegram.value = data.telegram_account || null

    Sentry.setUser({
      email: data.email,
      id: data.id,
      username: data.name
    })
  }

  async function FetchBalancesRange(range: string) {
    const balancesResponse = await getBalancesRange(range)
    balances.value = balancesResponse.data
    return balancesResponse.data
  }

  async function FetchStatistics(period: string) {
    const response = await getStatistics(period)
    updateStateStatistics(response.data)
  }

  async function GetUserInfo(withApiToken: boolean) {
    const response = await getUserInfo(withApiToken)
    updateState(response.data.attributes, withApiToken)
  }

  async function TogglePaymentsStatus() {
    const response = await togglePaymentsStatus()
    updateState(response.data.attributes)
  }

  async function ToggleFrozenStatus() {
    const response = await toggleFrozenStatus()
    updateState(response.data.attributes)
  }

  async function CheckUser() {
    if (!userId.value) {
      await GetUserInfo(false)
    }
    return true
  }

  async function Become(payload: IBecomePayload) {
    try {
      await becomeUser(payload)
      return true
    } catch (e) {
      return true
    }
  }

  async function FetchBalances(period: string) {
    const response = await getBalances(period)
    balances.value = response.data
  }

  async function LogOut() {
    await logout()
    userId.value = 0
  }

  async function LogIn(params: ILogin) {
    const response = await login(params)
    userId.value = parseInt(response.data.id)
  }

  async function SignUp(params: IRegistration) {
    await registration(params)
  }

  async function ConfirmAccount(token: string) {
    await confirmAccount(token)
  }

  async function ResetPassword(params: IResetPasswordAttributes) {
    await resetPassword(params)
  }

  async function SendRecoveryLetter(email: string) {
    await sendRecovery(email)
  }

  async function SendConfirmationLetter(email: string) {
    await sendConfirmation(email)
  }

  async function Unsubscribe(token: string) {
    await unsubscribe(token)
    unsubscribed.value = true
  }

  async function CheckSubscription(token: string) {
    const response = await checkSubscription(token)
    unsubscribed.value = response.unsubscribed
  }

  async function UpdateContactState() {
    withValidContact.value = true
  }

  async function ChangePassword(params: IPasswordAttributes) {
    return changePassword(params)
  }

  async function EnableTwoFa(params: ITwoFaAttributes) {
    await enableTwoFa(params)
    userId.value = 0
  }

  async function DisableTwoFa(params: ITwoFaAttributes) {
    const response = await disableTwoFa(params)
    otpInfo.value = response.data.attributes.otp
  }

  async function EnableApiToken() {
    const response = await enableApiToken()
    updateState(response.data.attributes, true)
  }

  async function RotateApiToken() {
    const response = await rotateApiToken()
    updateState(response.data.attributes, true)
  }

  async function DisableApiToken() {
    const response = await disableApiToken()
    updateState(response.data.attributes, true)
  }

  async function GetBack() {
    return getBack()
  }

  async function FetchUserOtpInfo() {
    const response = await getUserOtpInfo()
    otpInfo.value = response.data.attributes.otp
  }

  async function SaveInfo(params: IUserInfoAttributes) {
    return saveInfo(params)
  }

  async function SetInitialTags(tags: string[]) {
    try {
      await setInitialTags(tags)
    } finally {
      showInitialSurveyModal.value = false
    }
  }

  async function SocialAuth({
    provider,
    response,
    referral,
    referer,
    utms,
    advert_type
  }: {
    provider: string
    response: any
    referral: any
    referer: any
    utms: any
    advert_type?: any
  }) {
    return socialAuth(provider, response, referral, referer, utms, advert_type)
  }

  async function HideMessage() {
    await setMessageAsRead()
    showSystemMessage.value = false
  }

  async function AcceptCurrentTerms() {
    await acceptCurrentTerms()
    termsAccepted.value = true
  }

  async function SaveNotifications(payload: any) {
    await saveNotifications(payload)
  }

  async function RequestMarketplace() {
    const response = await requestMarketplace()
    updateState(response.data.attributes)
  }

  async function RequestCall(channel: string) {
    const response = await requestCall(channel)
    updateState(response.data.attributes)
  }

  async function ToggleMarketPlace(status: boolean) {
    const response = await toggleMarket(status)
    updateState(response.data.attributes)
  }

  async function GetTelegramLink() {
    return getTelegramLink()
  }

  async function DisconnectTelegram() {
    const response = await disconnectTelegram()
    updateState(response.data.attributes)
  }

  async function FetchUserInfo() {
    const response = await getCurrentUserInfo()
    info.value = response.data.attributes
  }

  function clearOnboardProcessingFlag() {
    isProcessingOnboarding.value = false
  }

  const userID = computed(() => {
    return userId.value
  })

  const isStaff = computed(() => {
    return staff.value
  })

  const isRtbUser = computed(() => {
    return rtb.value
  })

  const allowBid = computed(() => {
    return marketplaceStatus.value === MarketplaceStatus.Enabled
  })

  const flagEnabled = computed(() => {
    return (id: string) => {
      return flags.value.includes(id)
    }
  })

  const featureEnabled = computed(() => {
    return (id: string) => {
      return enabledFeatures.value.includes(id)
    }
  })

  const widgetEnabled = computed(() => {
    return showWidget.value && widgetData.value
  })

  return {
    apiToken,
    apiEnabled,
    termsAccepted,
    userID,
    isStaff,
    showInitialSurveyModal,
    isRtbUser,
    allowBid,
    flagEnabled,
    name,
    email,
    roles,
    balance,
    marketplaceBalance,
    serviceFeeBalance,
    payoutsBalance,
    replenishmentsBalance,
    withValidContact,
    paymentsUnderReview,
    skipCpmCheck,
    allowSmartCpm,
    useConv,
    statConversionTypes,
    referrerCode,
    staff,
    rtb,
    showSystemMessage,
    systemMessage,
    info,
    otpInfo,
    suid,
    availableCpmTypes,
    tourId,
    onboardingStep,
    onboardingStatus,
    notifications,
    balances,
    withConfirmationRequests,
    availableToWithdrawn,
    marketplaceStatus,
    enabledFeatures,
    flatDealAdTypeIds,
    hasTabFlatDeal,
    hadInitialTalk,
    initialTalkRequested,
    partnersEnabled,
    dealsEnabled,
    flags,
    adScanHits,
    nextUsageFeeDate,
    frozen,
    allowedPayments,
    defaultNotifications,
    showWidget,
    widgetData,
    verifyPaymentData,
    isNewDirect,
    testTargetingAllowed,
    extendedSettingsAllowed,
    usageTaxPayed,
    connectedMessengers,
    unsubscribed,
    telegram,
    isWidgetScriptLoaded,
    GetUserInfo,
    TogglePaymentsStatus,
    ToggleFrozenStatus,
    CheckUser,
    Become,
    FetchBalances,
    LogOut,
    LogIn,
    SignUp,
    ConfirmAccount,
    ResetPassword,
    SendRecoveryLetter,
    SendConfirmationLetter,
    Unsubscribe,
    CheckSubscription,
    UpdateContactState,
    ChangePassword,
    EnableTwoFa,
    DisableTwoFa,
    EnableApiToken,
    RotateApiToken,
    DisableApiToken,
    GetBack,
    FetchUserOtpInfo,
    SaveInfo,
    SocialAuth,
    HideMessage,
    AcceptCurrentTerms,
    SaveNotifications,
    RequestMarketplace,
    RequestCall,
    ToggleMarketPlace,
    GetTelegramLink,
    DisconnectTelegram,
    FetchUserInfo,
    referralAdvert,
    referralPublisher,
    FetchBalancesRange,
    FetchStatistics,
    widgetEnabled,
    SetInitialTags,
    featureEnabled,
    clearOnboardProcessingFlag,
    isProcessingOnboarding
  }
})
